import { useEffect, useRef, useState } from "react";
import { ChevronDown, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { STATUS_TONE } from "../lib/api";

/** The status IS the control. Click it to move the project; locked (visible, not clickable) for
 *  anyone outside the roles an admin has allowed in Settings › Permissions. */
export default function StatusMenu({ project, onPick, onRequestOpen, hideLabel = false, align = "left" }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const st = project.status;
  const may = st.can_change;

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const badge = (
    <span data-testid="ops-status-badge"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
            STATUS_TONE[st.status] || ""}`}>
      {st.label}
      {may ? <ChevronDown className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3 opacity-60" />}
    </span>
  );

  return (
    <div className="relative flex items-center gap-2" ref={box}>
      {!hideLabel && <span className="text-xs font-medium text-slate-500">Status</span>}
      {may ? (
        <button onClick={() => setOpen((v) => !v)} data-testid="ops-status-trigger">{badge}</button>
      ) : (
        <span title={`Only ${(st.who_may_change || []).join(", ")} can change the status`}
              data-testid="ops-status-locked">{badge}</span>
      )}
      {st.reason && <span className="text-xs italic text-slate-400">“{st.reason}”</span>}

      {open && may && (
        <div className={`absolute ${align === "right" ? "right-0" : "left-12"} top-8 z-40 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg`}
             data-testid="ops-status-menu">
          {st.status === "estimate" && (
            <button data-testid="ops-status-request-open"
                    onClick={() => { setOpen(false); onRequestOpen(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
              <Mail className="h-3.5 w-3.5 text-slate-400" /> Request to Open
            </button>
          )}
          {(st.transitions || []).length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500">
              This job is finished — there is nowhere left to move it.
            </p>
          )}
          {(st.transitions || []).map((t) => (
            <button key={t.status} data-testid={`ops-status-option-${t.status}`}
                    onClick={() => {
                      if (!t.allowed) { toast.error(`Only ${t.who} can set a job ${t.label}`); return; }
                      setOpen(false);
                      onPick(t);
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                      t.allowed ? "text-slate-700 hover:bg-slate-50"
                        : "cursor-not-allowed text-slate-400"}`}>
              <span>{t.label}</span>
              <span className="text-[11px] text-slate-400">
                {!t.allowed ? `only ${t.who}` : t.reason_required ? "reason required" : ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
